import { Alert, Button, Input, Space, Tabs } from 'antd'
import { ReloadOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../../../auth/AuthProvider'
import { isSysAdmin, isWorkspaceAdmin, isWorkspaceApprover, canRequestTermination } from '../../../utils/roles'
import { Loading } from '../../../components/Loading'
import { EmptyState } from '../../../components/EmptyState'
import { TerminateWorkspaceModal } from './TerminateWorkspaceModal'
import { TerminationRequestsTable } from './TerminationRequestsTable'
import { getAllTerminationRequests, getMyTerminationRequests } from '../services/termination-request-api'
import type { TerminationRequest, TerminationRequestStatus } from '../types/termination-request.types'

export function TerminationRequestsList() {
  const { user } = useAuth()
  const [requests, setRequests] = useState<TerminationRequest[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [initialLoad, setInitialLoad] = useState(true)
  const [editRequest, setEditRequest] = useState<TerminationRequest | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Derive permissions from JWT claims
  const isSysAdminUser = isSysAdmin(user)
  const isWorkspaceAdminUser = isWorkspaceAdmin(user)
  const isWorkspaceApproverUser = isWorkspaceApprover(user)

  // workspace_admin and workspace_approver can view all requests
  const canViewAllRequests = isSysAdminUser || isWorkspaceAdminUser || isWorkspaceApproverUser

  // workspace_approver can approve/reject requests
  const canApprove = isSysAdminUser || isWorkspaceApproverUser

  // Only Finance (or sysadmin) can submit a termination request
  const canRequest = canRequestTermination(user)

  // Default tab based on permissions
  const [activeTab, setActiveTab] = useState<string>(canViewAllRequests ? 'all' : 'my')

  const filteredRequests = useMemo(() => {
    if (!searchTerm.trim()) return requests

    const search = searchTerm.toLowerCase().trim()
    return requests.filter(
      request =>
        request.tenantCode?.toLowerCase().includes(search) ||
        request.workspaceCode?.toLowerCase().includes(search) ||
        request.workspaceName?.toLowerCase().includes(search) ||
        request.requestedByName?.toLowerCase().includes(search)
    )
  }, [requests, searchTerm])

  const loadRequests = async (status?: TerminationRequestStatus) => {
    try {
      setLoading(true)
      setError(null)
      let data: TerminationRequest[]

      if (canViewAllRequests && activeTab !== 'my') {
        data = await getAllTerminationRequests(status)
      } else {
        data = await getMyTerminationRequests()
        if (status) {
          data = data.filter(r => r.status === status)
        }
      }

      setRequests(data)
    } catch (err) {
      console.error('Failed to load requests:', err)
      setError(err as Error)
    } finally {
      setLoading(false)
      setInitialLoad(false)
    }
  }

  useEffect(() => {
    const status = activeTab === 'all' || activeTab === 'my' ? undefined : (activeTab as TerminationRequestStatus)
    loadRequests(status)
  }, [activeTab])

  const handleTabChange = (key: string) => {
    setActiveTab(key)
  }

  const tabItems = [
    ...(canViewAllRequests
      ? [
          { label: 'All requests', key: 'all' },
          { label: 'Pending', key: 'p' },
          { label: 'Approved', key: 'a' },
          { label: 'My requests', key: 'my' }
        ]
      : [{ label: 'My requests', key: 'my' }])
  ]

  if (error) {
    return (
      <Alert
        message="Error loading termination requests"
        description={error.message}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={() => loadRequests()}>
            Retry
          </Button>
        }
      />
    )
  }

  if (initialLoad && loading) {
    return <Loading />
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 112px)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          flexShrink: 0
        }}
      >
        <Space size="middle">
          <Input
            placeholder="Search by tenant code, workspace code, name or requester..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ width: 350 }}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={() => loadRequests()} loading={loading}>
            Refresh
          </Button>
          {canRequest && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
              Terminate workspace
            </Button>
          )}
        </Space>
      </div>

      <Tabs activeKey={activeTab} items={tabItems} onChange={handleTabChange} style={{ flexShrink: 0 }} />

      <div style={{ flex: 1, minHeight: 0 }}>
        {filteredRequests.length === 0 && !loading ? (
          <EmptyState
            description={
              searchTerm ? `No requests found matching "${searchTerm}"` : 'No requests found for this filter'
            }
          />
        ) : (
          <TerminationRequestsTable
            requests={filteredRequests}
            loading={loading}
            onRefresh={loadRequests}
            onEdit={request => {
              setEditRequest(request)
              setModalOpen(true)
            }}
            canApprove={canApprove}
          />
        )}
      </div>

      <TerminateWorkspaceModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setEditRequest(null)
        }}
        onSuccess={() => loadRequests()}
        editRequest={editRequest}
      />
    </div>
  )
}
